import { CartEvent } from "@/global/store/data/purchaseSeats/cartManager";
import { throttleTime } from "@/global/utils/utils";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { InventoryType } from "@/global/store/data/types";
import { CheckoutInventoryCard } from "../../Checkout/YourTickets/CheckoutInventoryCard";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { createState } from "@/global/store/zustand_store";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";

export interface DiffSeatsModalResponse {
  action: "tryAnotherSeat" | "acceptSuggestedSeat";
}

const useDiffSeatsModalState = createState({
  isOpen: false,
  cartEventActive: null as CartEvent | null,
  onFinish: (a: DiffSeatsModalResponse) => {},
});

export function useDiffSeatsModal() {
  const { isOpen, onFinish, set, reset, ...modalState } =
    useDiffSeatsModalState();
  const execute = (args: typeof modalState) => {
    return new Promise<DiffSeatsModalResponse>((resolve) => {
      set({ ...args, isOpen: true, onFinish: resolve });
    });
  };
  return { ...modalState, isOpen, execute, onFinish, set, reset };
}

export function DiffSeatsAlertModal() {
  const teamConfig = useTeamConfig();
  const { sm: smDown } = useCssMediaQuery("down");
  const modalState = useDiffSeatsModal();
  const cartEventActive = modalState?.cartEventActive;

  const handleClose = () => {
    modalState.set({ isOpen: false });
    throttleTime(400, () => modalState.reset());
  };

  const onClickTryAnotherSeat = () => {
    handleClose();
    modalState.onFinish({ action: "tryAnotherSeat" });
  };

  const onClickAcceptSuggestedSeat = () => {
    handleClose();
    modalState.onFinish({ action: "acceptSuggestedSeat" });
  };

  return (
    <NotificationModal
      isOpen={modalState.isOpen}
      hideTitleIcon
      title={
        teamConfig?.languages?.diffSeatsAlertModal?.title ||
        "New Option, Same Great View"
      }
      description={
        <>
          <Box>
            {teamConfig?.languages?.diffSeatsAlertModal?.description || (
              <Typography>
                Looks like your selection is no longer available - but we've
                found another great option for you.
              </Typography>
            )}
          </Box>
          <Box mt={2}>
            <Grid container spacing={2}>
              {cartEventActive?.products?.map((inv: InventoryType) => {
                return (
                  <Grid item xs={12} key={inv?.id}>
                    <CheckoutInventoryCard
                      inventory={inv}
                      cartEvent={cartEventActive}
                      isRetry={true}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </>
      }
      secondaryButtonText={"Pick Another"}
      primaryButtonText={"Accept Suggestion"}
      closeIconProps={{
        onClick: onClickTryAnotherSeat,
      }}
      secondaryButtonProps={{
        variant: "outlined",
        style: { width: smDown ? "100%" : "inherit" },
        onClick: onClickTryAnotherSeat,
      }}
      primaryButtonProps={{
        style: { width: smDown ? "100%" : "inherit" },
        onClick: onClickAcceptSuggestedSeat,
      }}
    />
  );
}
